import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchLeaderboard, fetchGroups } from '@/api/leaderboard'
import { LeaderboardTable } from '@/components/leaderboard/LeaderboardTable'
import { Spinner } from '@/components/ui/Spinner'
import { useSessionStore } from '@/store/sessionStore'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import type { LeaderboardEntry, PlayerGroup } from '@/types/scoring'
import { MAX_SCORE } from '@/types/scoring'

export function LeaderboardPage() {
  const { t } = useTranslation()
  const { playerId, displayName, groupId: myGroupId } = useSessionStore()
  const [groups, setGroups] = useState<PlayerGroup[]>([])
  // Initialise from session store immediately — avoids a double-fetch on mount
  const [selectedGroup, setSelectedGroup] = useState<string | 'all'>(myGroupId ?? 'all')
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  // Load group tab list once
  useEffect(() => {
    fetchGroups().then(gs => {
      setGroups(gs)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const data = await fetchLeaderboard(selectedGroup === 'all' ? undefined : selectedGroup)
        if (!cancelled) {
          setEntries(data)
          setLastUpdated(new Date())
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    const id = setInterval(load, 30_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [selectedGroup])

  const myEntry = entries.find(e => e.player_id === playerId)

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏅</span>
            <div>
              <h1 className="text-base font-black text-white leading-tight">{t('leaderboard.title')}</h1>
              <p className="text-[11px] text-white/40">
                {lastUpdated ? t('leaderboard.updated', { time: lastUpdated.toLocaleTimeString() }) : t('leaderboard.loading')}
              </p>
            </div>
          </div>
          <nav className="flex gap-2 items-center">
            <LangSwitcher />
            <a href="/standings" className="btn-secondary btn-sm text-sm">📊 {t('standings.nav')}</a>
            <a href="/champion-board" className="btn-secondary btn-sm text-sm">{t('leaderboard.championBoard')}</a>
            <a href="/bracket" className="btn-secondary btn-sm text-sm">⚽ {t('nav.bracket')}</a>
          </nav>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Group tabs */}
        {groups.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-6">
            <button
              onClick={() => setSelectedGroup('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                selectedGroup === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
              }`}
            >
              {t('leaderboard.global')}
            </button>
            {groups.map(g => (
              <button
                key={g.id}
                onClick={() => setSelectedGroup(g.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  selectedGroup === g.id
                    ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/40'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
                }`}
              >
                {g.id === myGroupId ? '★ ' : ''}{g.name}
              </button>
            ))}
          </div>
        )}

        {/* Your position card */}
        {myEntry && (
          <div className="card p-4 mb-6 border-emerald-500/30 bg-emerald-500/5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 mb-0.5">{t('leaderboard.yourPosition')}</p>
                <p className="text-lg font-bold text-emerald-400">{displayName}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-gold-400">{myEntry.total_points}</div>
                <div className="text-xs text-white/40">{t('leaderboard.maxPts', { max: MAX_SCORE })}</div>
              </div>
              <div className="text-4xl font-black text-white/20">#{myEntry.rank}</div>
            </div>
          </div>
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-white">{entries.filter(e => e.is_ai === 0).length}</div>
            <div className="text-[10px] text-white/40">{t('leaderboard.players')}</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-emerald-400">
              {entries.filter(e => e.is_ai === 0 && e.r32_picks > 0).length}
            </div>
            <div className="text-[10px] text-white/40">{t('leaderboard.submitted')}</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-gold-400">
              {entries[0]?.total_points ?? 0}
            </div>
            <div className="text-[10px] text-white/40">{t('leaderboard.leaderPts')}</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-purple-400">
              {entries.filter(e => e.is_ai === 1).length}
            </div>
            <div className="text-[10px] text-white/40">{t('leaderboard.aiPlayers')}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 text-white/40">
            <div className="text-4xl mb-3">📋</div>
            <p>{t('leaderboard.noPicksYet')}</p>
          </div>
        ) : (
          <LeaderboardTable entries={entries} currentPlayerId={playerId ?? undefined} />
        )}
      </div>
    </div>
  )
}
